/**
 * 学生端 H5(二期 · 原型 student/mobile-h5):同一套路由、同一批页面,只按「是不是手机」换版式。
 * 「是不是手机」只在这里判断一处,页面与组件一律读 stores/viewport 的结果,不各自量屏幕。
 *
 * 判定(★ 口径,见 docs/phase2/mobile-h5.md):手机 = 触屏设备 且 屏幕窄。
 *  - 触屏设备:主指针是手指(`pointer: coarse`),或者浏览器自报是移动端(UA);
 *  - 屏幕窄:视口宽 ≤ 767(竖屏);或者是自报移动端的浏览器、屏幕短边 ≤ 500 且视口宽 ≤ 1023
 *    (手机横过来还是手机版式,不会转一下就变成 1440 画板;按屏幕短边认,弹出输入法不会来回切)。
 * 电脑(鼠标 / 触控板)不管窗口拉多窄都还是 1440 画板 —— 一期电脑端的版式一个像素都不变;
 * 平板(≥ 768)也还是画板;ScratchJr 的平板端有自己的页面(/jr/…,自己管版式,不走这里的手机版式)。
 * 手机浏览器点「请求桌面版网站」后 UA 与视口都变成桌面的,自然回到画板。
 * 调试 / 截图:地址带 ?h5=1 强制手机版式、?h5=0 强制画板、?h5=auto 取消(只记在本标签页)。
 */
export interface ViewportEnv {
  /** 视口宽(CSS 像素;取 documentElement.clientWidth,页面内容再宽它也不变) */
  width: number;
  /** 屏幕短边(CSS 像素;不受输入法弹出影响);拿不到给 0 */
  shortSide: number;
  /** 主指针是手指(触屏) */
  coarse: boolean;
  /** 浏览器自报移动端 */
  mobileUa: boolean;
}

/** 竖屏:视口宽不超过它算手机 */
export const PHONE_MAX_WIDTH = 767;
/** 横屏:屏幕短边不超过它、且视口宽不超过下面这个数,算手机横屏 */
export const PHONE_MAX_SHORT_SIDE = 500;
export const PHONE_LANDSCAPE_MAX_WIDTH = 1023;

/** 手动指定(调试 / 截图):phone = 强制手机版式,desktop = 强制画板 */
export type ViewOverride = 'phone' | 'desktop';

/** UA 里自报移动端(手机浏览器、微信内置浏览器等);iPad 新系统自报成 Mac,靠触屏那一条认 */
export function isMobileUa(ua: string): boolean {
  return /Mobi|Android|iPhone|iPod|HarmonyOS|Windows Phone/i.test(ua);
}

/** 是不是手机(见文件头的口径);override 优先 */
export function isPhone(env: ViewportEnv, override: ViewOverride | null = null): boolean {
  if (override) return override === 'phone';
  if (!env.coarse && !env.mobileUa) return false;
  if (!(env.width > 0)) return false;
  if (env.width <= PHONE_MAX_WIDTH) return true;
  // 横屏只认自报移动端的浏览器:手机点了「请求桌面版网站」(UA 变桌面、视口约 980)就不再算
  return (
    env.mobileUa &&
    env.shortSide > 0 &&
    env.shortSide <= PHONE_MAX_SHORT_SIDE &&
    env.width <= PHONE_LANDSCAPE_MAX_WIDTH
  );
}

/**
 * 地址里的 ?h5=:1 / on / phone → 强制手机版式;0 / off / pc → 强制画板;auto → 取消手动指定;
 * 没带或别的值 → undefined(不动现有的)。
 */
export function parseViewOverride(v: unknown): ViewOverride | 'auto' | undefined {
  if (typeof v !== 'string') return undefined;
  const s = v.trim().toLowerCase();
  if (['1', 'on', 'phone', 'true'].includes(s)) return 'phone';
  if (['0', 'off', 'pc', 'false'].includes(s)) return 'desktop';
  if (s === 'auto') return 'auto';
  return undefined;
}

/**
 * 一个路由在手机上怎么办(路由 meta.h5):
 *  - true → 'phone':有手机版式;
 *  - 'pc' → 'pc-only':这页只能在电脑上用(考试检录 / 答题:要摄像头、屏幕共享,手机留着当第三视角),手机来了去说明页;
 *  - 没标 → 'board':不归学生端 H5 管(后台、官网、家长端、大赛官网……),照旧 1440 画板。
 * 不是手机一律 'board'。
 */
export type H5Layout = 'phone' | 'pc-only' | 'board';
export function h5LayoutOf(metaH5: unknown, phone: boolean): H5Layout {
  if (!phone) return 'board';
  if (metaH5 === true) return 'phone';
  if (metaH5 === 'pc') return 'pc-only';
  return 'board';
}

/** 接口与埋点的端标识(x-client):手机版式下是 h5,其余是 web;服务端按它记埋点的 client 维度 */
export function clientKindOf(phone: boolean): 'web' | 'h5' {
  return phone ? 'h5' : 'web';
}
